import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Button, Form, RadioLabel, CurrencyInput, ToastContext, TooltipHelp } from '@weezevent/nacre';
import { requiredField, useForm } from '../../utils/validators';

import Styles from '../../styles/eventCreation/eventCreation.module.css';
import CurrencySelector from '../../components/rates/currencySelector';
import { SendMailAccountActivationModal } from '../../components/template/contextMenu/event/sendMailAccountActivationModal';
import { OrganizationMustBeConfirmedModal } from '../../components/template/contextMenu/event/organizationMustBeConfirmedModal';
import OrganizationInfos from '../../models/organizationInfos';
import RateMaxPrice from '../../models/rateMaxPrice';
import { useSegment } from '../../components/tracking/trackingProvider';
import { trackRateType } from './utils';
import SelectTaxes from '../../components/tax/selectTaxes';
import TaxModel from '../../models/taxModel';
import RateError from '../rates/components/rateError';

export default function EventCreationRates({ onChange, onDone, formEvent, isSubmitting, organization, rateTypes, organizationInfos, onSubmitting, onCardDone = () => {} }) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const [currency, setCurrency] = React.useState(organizationInfos.currency || 'EUR');
    const [sendMailAccountActivationModal, setSendMailAccountActivationModal] = React.useState(false);
    const [organizationMustBeConfirmedModal, setOrganizationMustBeConfirmedModal] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [rateModified, setRateModified] = React.useState(false);
    const [taxes, loadingTaxes] = TaxModel.useApiModel({});

    const validateTax = React.useCallback((name, value, formRate) => {
        if (formRate.type === 'fixed') {
            return formRate.tax_id !== null ? true : 'ticket.creationEvent.rates.taxError';
        }
        return true;
    }, []);

    const validators = React.useMemo(() => {
        return {
            name: requiredField,
            tax_id: validateTax
        };
    }, [validateTax]);

    const [formRate, setRateForm, setRateObject, rateErrors, rateValidate] = useForm(
        {
            type: 'fixed',
            name: t('ticket.creationEvent.rates.priceNamePaid'),
            price: 10,
            maxPrice: 3000,
            inventories: [],
            tax_id: null
        },
        validators
    );

    const onChangeRate = React.useCallback(
        _formRate => {
            setRateObject(_formRate, true);
            let tmp = { ...formRate, ..._formRate };
            onChange('rate', tmp);
        },
        [formRate, onChange, setRateObject]
    );

    React.useEffect(() => {
        if (!formEvent?.rate) {
            if (formEvent?.subtype === 'online_donations') {
                onChangeRate(rateTypes.open, true);
            } else {
                onChangeRate(rateTypes.fixed, true);
            }
        }
    }, [formEvent, setRateObject, onChangeRate]);

    //this useMemo allows to display or aps the publish button. These mistakes aren't meant to happen
    const displayPublishButton = React.useMemo(() => {
        let display = true;
        organizationInfos.can_publish_errors.forEach(error => {
            display = display && !['organization_deleted', 'has_not_organisateurs_prospection', 'not_confirmed_cgus'].includes(error);
        });

        return display;
    }, [organizationInfos]);

    const canPublish = React.useCallback(_organizationInfos => {
        if (_organizationInfos.can_publish) {
            return true;
        }

        if (_organizationInfos.can_publish_errors.includes('account_not_validated')) {
            setSendMailAccountActivationModal(true);
            return false;
        }

        // This case of organization not validated, will only occur if the owner has not validated his account,
        // because the validation of the account (Account), automatically validates the organization
        if (!_organizationInfos.is_owner && _organizationInfos.can_publish_errors.includes('organization_not_validated')) {
            setOrganizationMustBeConfirmedModal(true);
            return false;
        }

        return false;
    }, []);

    const saveOrganization = React.useCallback(
        (organizationInfos, toPublish) => {
            const previousCurrency = organizationInfos?.currency;
            const previousTimezone = organizationInfos?.timezone;
            const previousCountry = organizationInfos?.address?.country;

            if (organizationInfos.has_event || (previousCurrency === currency && previousTimezone === formEvent.timezone && previousCountry === formEvent.address.country)) {
                return onDone(toPublish);
            }
            if (!organizationInfos?.already_sold) {
                organization.currency = currency;
            }
            organization.timezone = formEvent.timezone || organization.timezone;
            if (formEvent.address.country) {
                organization.address = { ...organization.address, country: formEvent.address.country };
            }
            organization
                .save({})
                .then(() => {
                    onDone(toPublish);
                })
                .catch(() => {
                    toast.error(t('currencies.error_update'));
                    onDone(toPublish);
                });
        },
        [toast, onDone, organization, currency, formEvent]
    );

    const handleSubmit = React.useCallback(
        toPublish => {
            const isValid = rateValidate();

            if (isValid) {
                onSubmitting(true);
                // if the can_publish is false and the user wants to publish,
                // we refresh organizationInfos in case the user has validated his account in the meantime.
                // This allows you not to lose the current form
                if (!organizationInfos.can_publish && toPublish) {
                    OrganizationInfos.get({ id: null, organizationId: organization.id, cache: false, allow_cache: false })
                        .then(response => {
                            let _canPublish = canPublish(response);
                            if (toPublish && !_canPublish) {
                                onSubmitting(false);
                                toast.warning(t('ticket.creationEvent.toast.cannotPublish'));
                            }

                            if (_canPublish) {
                                saveOrganization(response, toPublish);
                            }
                        })
                        .catch(() => {
                            toast.error(t('ticket.creationEvent.toast.orgaInfosError'));
                            onSubmitting(false);
                        });
                } else {
                    saveOrganization(organizationInfos, toPublish);
                }
            }
        },
        [organizationInfos, toast, organization, onSubmitting, canPublish, saveOrganization, rateValidate]
    );

    RateMaxPrice.useApiModel(
        {
            eventId: formEvent ? formEvent.id : null,
            organizationId: organization.id,
            launch: organization.id && formEvent.id,
            onSuccess: result => {
                setRateForm('maxPrice', result.max_price);
            }
        },
        [formEvent, organization]
    );

    const rateLimit = React.useMemo(() => {
        return formRate.price > formRate.maxPrice;
    }, [formRate]);

    return (
        <>
            <SendMailAccountActivationModal
                open={sendMailAccountActivationModal}
                onSubmit={() => {}}
                onSave={() => handleSubmit(false)}
                onClose={() => setSendMailAccountActivationModal(false)}
                createMode
            />
            <OrganizationMustBeConfirmedModal
                onSave={() => handleSubmit(false)}
                open={organizationMustBeConfirmedModal}
                onClose={() => setOrganizationMustBeConfirmedModal(false)}
                createMode
            />
            <Form.Field style={{ marginBottom: '0' }}>
                <Form.Label label={t('ticket.creationEvent.rates.formLabelType')} />
                <div>
                    {Object.keys(rateTypes).map(key => {
                        const rate_type = rateTypes[key];
                        return (
                            <RadioLabel
                                className={Styles['radio-label-custom']}
                                key={rate_type.type}
                                active={formRate?.type === rate_type.type}
                                inline
                                bordered
                                name={rate_type.type}
                                checked={formRate?.type === rate_type.type}
                                label={rate_type.name}
                                onClick={() => {
                                    const rateInstance = rate_type;
                                    if (rateModified) {
                                        rateInstance.name = formRate.name;
                                    }
                                    onChangeRate(rateInstance);
                                    track('ACTION > SELECT RATE TYPE', {
                                        'Rate type': trackRateType(rate_type.type),
                                        'Page Title': 'CREATE EVENT'
                                    });
                                }}
                            />
                        );
                    })}
                </div>
            </Form.Field>
            <Form.Field className={Styles['form-input-rate-name']}>
                <Form.Label label={t('ticket.creationEvent.rates.formLabelName')} required />
                <Form.Input
                    name="name"
                    placeholder={t('ticket.creationEvent.rates.priceNamePaid')}
                    value={formRate?.name}
                    onChange={({ target: { value } }) => {
                        if (value.length >= 40) {
                            // max length
                            return;
                        }
                        onChangeRate({ name: value });
                        setRateModified(true);
                    }}
                    errors={!!rateErrors['name']}
                />
                <Form.FieldErrors errors={rateErrors['name'] ? [t(rateErrors['name'])] : []} />
            </Form.Field>
            <div className={Styles['container-rate-inputs']}>
                <Form.Field>
                    <Form.Label
                        label={formRate?.type !== 'open' ? t('ticket.creationEvent.rates.formLabelPrice') : t('ticket.creationEvent.rates.formLabelPriceMin')}
                        disabled={formRate?.type === 'free'}
                    />
                    <div style={{ display: 'flex', marginBottom: '10px' }}>
                        <CurrencyInput
                            className={Styles['price-input']}
                            disabled={formRate?.type === 'free'}
                            name="price"
                            locale={i18n.language}
                            currency={currency}
                            value={formRate?.price}
                            onChange={data => {
                                onChangeRate({ price: +data.value });
                            }}
                        />
                        {!organizationInfos?.already_sold && (
                            <Form.Field className={Styles['rates-inputs-currency']}>
                                <CurrencySelector value={currency} onChange={setCurrency} />
                            </Form.Field>
                        )}
                    </div>
                    {rateLimit && <RateError event={formEvent} maxPrice={formRate.maxPrice} />}
                    {formRate.price < 1 && formRate.type !== 'free' && <RateError event={formEvent} price={formRate.price} currency={currency} />}
                </Form.Field>
                {formRate?.type === 'fixed' && (
                    <Form.Field>
                        <SelectTaxes
                            isLoading={loadingTaxes}
                            label={
                                <div style={{ display: 'flex' }}>
                                    <Form.Label label={t('ticket.rates.infos.eventTax')} required />
                                    <TooltipHelp title={t('ticket.creationEvent.rates.taxTooltip')} mini />
                                </div>
                            }
                            disabled={formRate?.type !== 'fixed'}
                            taxes={taxes}
                            onChange={(optionValue, option) => onChangeRate({ tax_id: optionValue })}
                            value={formRate.tax_id}
                            eventCountry={formEvent?.address?.country}
                            errors={!!rateErrors['tax_id']}
                        />
                        <Form.FieldErrors errors={rateErrors['tax_id'] ? [t(rateErrors['tax_id'])] : []} />
                    </Form.Field>
                )}
            </div>
            {displayPublishButton ? (
                <div className={Styles['buttons-submit']}>
                    <Button primary className={Styles['button-publish']} label={t('ticket.creationEvent.publish')} onClick={() => handleSubmit(true)} isLoading={isSubmitting} />
                    <Button
                        className={Styles['button-save-continue']}
                        label={t('ticket.creationEvent.saveAndContinue')}
                        onClick={() => handleSubmit(false)}
                        isLoading={isSubmitting}
                    />
                </div>
            ) : (
                <Button
                    primary
                    className={Styles['button-publish']}
                    label={t('ticket.creationEvent.saveAndContinue')}
                    onClick={() => handleSubmit(false)}
                    isLoading={isSubmitting}
                />
            )}
        </>
    );
}

EventCreationRates.propTypes = {
    onChange: PropTypes.func.isRequired,
    onDone: PropTypes.func.isRequired,
    onCardDone: PropTypes.func,
    formEvent: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    onSubmitting: PropTypes.func.isRequired,
    organization: PropTypes.object.isRequired,
    organizationInfos: PropTypes.object.isRequired,
    rateTypes: PropTypes.object.isRequired
};
